import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { AuthScreen } from './components/AuthScreen';
import { SetupScreen } from './components/SetupScreen';
import { ChatInterface } from './components/ChatInterface';
import { FeedbackScreen } from './components/FeedbackScreen';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { AdminScreen } from './components/AdminScreen';
import { ExportPortal } from './components/ExportPortal';
import { ChangePasswordScreen } from './components/ChangePasswordScreen';
import { SimulationSettings, CustomerProfile, FeedbackData, SavedSession } from './types';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';

export const App: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const [currentScreen, setCurrentScreen] = useState<'auth' | 'dashboard' | 'setup' | 'interaction' | 'feedback' | 'settings' | 'admin' | 'change-password' | 'export'>('auth');
    const [simulationSettings, setSimulationSettings] = useState<SimulationSettings | null>(null);
    const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
    const [chatSessionId, setChatSessionId] = useState<string | null>(null);
    const [systemPrompt, setSystemPrompt] = useState<string>('');
    const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null);
    const [timingDetails, setTimingDetails] = useState<string>('');

    const [userFullName, setUserFullName] = useState<string>('');
    const [userTeam, setUserTeam] = useState<string>('');
    const [isAdmin, setIsAdmin] = useState<boolean>(false); // Add this

    // For viewing historical sessions
    const [viewingSavedSession, setViewingSavedSession] = useState<boolean>(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                // Initial check for hardcoded admin emails
                const isHardcodedAdmin = currentUser.email === 'admin12345@simulate2.com';
                setIsAdmin(isHardcodedAdmin);

                // Fetch user specifics
                try {
                    const userDocRef = doc(db, "users", currentUser.uid);
                    const userDocSnap = await getDoc(userDocRef);

                    if (userDocSnap.exists()) {
                        const userData = userDocSnap.data();
                        setUserFullName(userData.fullName || '');
                        setUserTeam(userData.teamName || userData.team || '');
                        // Merge Firestore isAdmin with hardcoded check
                        setIsAdmin(userData.isAdmin === true || isHardcodedAdmin);
                        
                        if (userData.requiresPasswordChange) {
                            setCurrentScreen('change-password');
                        } else if (currentScreen === 'auth') {
                            setCurrentScreen('dashboard');
                        }
                    } else if (currentScreen === 'auth') {
                        setCurrentScreen('dashboard');
                    }
                } catch (e) {
                    console.error("Error fetching user details:", e);
                    if (currentScreen === 'auth') setCurrentScreen('dashboard');
                }
            } else {
                setCurrentScreen('auth');
                setUserFullName('');
                setUserTeam('');
                setIsAdmin(false);
            }
        });
        return () => unsubscribe();
    }, []); // Only run once on mount

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setCurrentScreen('auth');
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    const handleSimulationStart = (settings: SimulationSettings, profile: CustomerProfile, sessionId: string | null, prompt: string) => {
        setSimulationSettings(settings);
        setCustomerProfile(profile);
        setChatSessionId(sessionId);
        setSystemPrompt(prompt);
        setCurrentScreen('interaction');
    };

    const handleFeedbackGenerated = async (data: FeedbackData | null, timing: string) => {
        console.log("Feedback generated. Data:", data);
        console.log("Current State - Settings:", simulationSettings, "Profile:", customerProfile, "User:", user?.uid);

        setFeedbackData(data);
        setTimingDetails(timing);

        // Save to Firestore
        if (data && user && simulationSettings && customerProfile) {
            try {
                const sessionData = {
                    userId: user.uid,
                    userName: simulationSettings.userName,
                    userEmail: user.email,
                    timestamp: serverTimestamp(),
                    settings: simulationSettings,
                    profile: customerProfile,
                    feedback: data,
                    duration: timing,
                    userTeam: userTeam // Explicitly save team for analytics
                };
                console.log("Attempting to save session to Firestore...", sessionData);
                const docRef = await addDoc(collection(db, "sessions"), sessionData);
                console.log("Session saved successfully with ID:", docRef.id);
            } catch (e) {
                console.error("Error saving session to Firestore:", e);
                // Optionally alert the user or show a toast
            }
        } else {
            console.warn("Skipping Firestore save. Missing required data:", {
                hasData: !!data,
                hasUser: !!user,
                hasSettings: !!simulationSettings,
                hasProfile: !!customerProfile
            });
        }

        setViewingSavedSession(false);
        setCurrentScreen('feedback');
    };

    const handleRestart = () => {
        setSimulationSettings(null);
        setCustomerProfile(null);
        setChatSessionId(null);
        setSystemPrompt('');
        setFeedbackData(null);
        setTimingDetails('');
        setViewingSavedSession(false);
        setCurrentScreen('setup');
    };

    const handleViewSavedSession = (session: SavedSession) => {
        setFeedbackData(session.feedback);
        setSimulationSettings(session.settings);
        setCustomerProfile(session.profile);
        setTimingDetails(session.duration);
        setViewingSavedSession(true);
        setCurrentScreen('feedback');
    };

    const handleNavigate = (screen: 'dashboard' | 'setup' | 'interaction' | 'feedback' | 'settings' | 'admin' | 'change-password' | 'export') => {
        setCurrentScreen(screen);
    };

    return (
        <div id="app-container">
            {user && currentScreen !== 'auth' && (
                <Sidebar
                    currentScreen={currentScreen}
                    isAdmin={isAdmin} // Pass isAdmin
                    onNavigate={handleNavigate}
                    onLogout={handleLogout}
                />
            )}

            <div className="main-content">
                {user && currentScreen !== 'auth' && (
                    <header>
                        <h1>
                            {currentScreen === 'dashboard' ? 'Dashboard' :
                                currentScreen === 'setup' ? 'Simulation Setup' :
                                    currentScreen === 'interaction' ? 'Live Session' :
                                        currentScreen === 'feedback' ? 'Expert Feedback' : 
                                            currentScreen === 'admin' ? 'Administration' : 
                                                currentScreen === 'export' ? 'Data Export Portal' : 'Settings'}
                        </h1>

                        <div className="header-user">
                            <div className="user-badge">
                                {user.email}
                                {isAdmin && <span style={{ marginLeft: '8px', padding: '2px 8px', background: 'var(--primary)', color: 'white', borderRadius: '12px', fontSize: '10px', fontWeight: 700, verticalAlign: 'middle' }}>ADMIN</span>}
                            </div>
                        </div>
                    </header>
                )}

                <main>
                    {currentScreen === 'auth' && (
                        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                            <AuthScreen onLoginSuccess={() => setCurrentScreen('dashboard')} />
                        </div>
                    )}

                    {currentScreen === 'change-password' && (
                        <ChangePasswordScreen onPasswordChanged={() => setCurrentScreen('dashboard')} />
                    )}

                    {currentScreen === 'dashboard' && user && (
                        <Dashboard
                            user={user}
                            onSessionClick={handleViewSavedSession}
                            onStartNewSimulation={() => setCurrentScreen('setup')}
                        />
                    )}

                    {currentScreen === 'setup' && user && (
                        <SetupScreen
                            userName={userFullName || user.email?.split('@')[0] || 'User'}
                            userTeam={userTeam || ''}
                            onSimulationStart={handleSimulationStart}
                        />
                    )}

                    {currentScreen === 'interaction' && simulationSettings && customerProfile && (
                        <ChatInterface
                            settings={simulationSettings}
                            profile={customerProfile}
                            chatSessionId={chatSessionId}
                            systemPrompt={systemPrompt}
                            onFeedbackGenerated={handleFeedbackGenerated}
                            onReset={handleRestart}
                        />
                    )}

                    {currentScreen === 'feedback' && simulationSettings && customerProfile && (
                        feedbackData ? (
                            <FeedbackScreen
                                feedbackData={feedbackData}
                                settings={simulationSettings}
                                profile={customerProfile}
                                timingDetails={timingDetails}
                                onRestart={viewingSavedSession ? () => setCurrentScreen('dashboard') : handleRestart}
                            />
                        ) : (
                            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                                <h2 style={{ marginBottom: '1rem' }}>Feedback Unavailable</h2>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                                    We encountered an error while analyzing your session. Please try restarting the simulation.
                                </p>
                                <button className="button button-primary" onClick={handleRestart}>
                                    Restart Simulation
                                </button>
                            </div>
                        )
                    )}

                    {currentScreen === 'admin' && isAdmin && (
                        <AdminScreen />
                    )}

                    {currentScreen === 'export' && isAdmin && (
                        <ExportPortal />
                    )}

                    {currentScreen === 'settings' && (
                        <div className="card">
                            <h2 style={{ marginBottom: '1rem' }}>Account Settings</h2>
                            <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>Configure your profile and application preferences.</p>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button className="button button-secondary" onClick={() => setCurrentScreen('dashboard')}>Return to Dashboard</button>
                                <button className="button button-danger" onClick={handleLogout}>Log Out</button>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};
